import { Dispatch, SetStateAction, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Input, Modal, ModalInner, SelectBox, Text } from 'components';
import { ListType } from 'types/selectbox';
import axios from 'common/AxiosAuth';

const CATEGORY_HINT_KEYS: Record<number, string> = {
  1: 'manageMail.categories.schedule',
  2: 'manageMail.categories.payment',
  3: 'manageMail.categories.inquiry',
  4: 'manageMail.categories.passResult'
};

interface ModalProps {
  isOpened: boolean;
  setIsOpened: Dispatch<SetStateAction<boolean>>;
  onRecipientAdded: () => Promise<void>;
}

interface RecipientData {
  name: string;
  email: string;
  mailCategory: string;
  mailType: string;
}

const AddRecipientModal = ({ isOpened, setIsOpened, onRecipientAdded }: ModalProps) => {
  const { t } = useTranslation();
  const [name, setName] = useState<string>('');
  const [email, setEmail] = useState<string>('');
  const [mailCategory, setMailCategory] = useState<number>(0);
  const [mailType, setMailType] = useState<number>(0);

  const categoryHint = useMemo(() => {
    const key = CATEGORY_HINT_KEYS[mailCategory];
    return key ? t(key) : t('manageMail.addModal.categorySelectHint');
  }, [mailCategory, t]);

  const categoryList: ListType[] = [
    { value: 1, label: 'SCHEDULE' },
    { value: 2, label: 'PAYMENT' },
    { value: 3, label: 'INQUIRY' },
    { value: 4, label: 'PASS_RESULT' }
  ];

  const typeList: ListType[] = [
    { value: 1, label: 'TO' },
    { value: 2, label: 'CC' },
    { value: 3, label: 'BCC' }
  ];

  const getCategoryValue = (value: number): string => {
    const category = categoryList.find(item => item.value === value);
    return category ? category.label : '';
  };

  const getTypeValue = (value: number): string => {
    const type = typeList.find(item => item.value === value);
    return type ? type.label : '';
  };

  const handleSubmit = async () => {
    if (!name || !email || !mailCategory || !mailType) {
      alert('모든 필드를 입력해주세요.');
      return;
    }

    const recipientData: RecipientData = {
      name,
      email,
      mailCategory: getCategoryValue(mailCategory),
      mailType: getTypeValue(mailType)
    };

    try {
      // * API 호출
      await axios.post('/api/admin/mail/receiver', recipientData);
      
      // * 성공 시 모달 닫기 및 데이터 새로고침
      setIsOpened(false);
      await onRecipientAdded();
      
      // * 폼 초기화
      setName('');
      setEmail('');
      setMailCategory(0);
      setMailType(0);
    } catch (error) {
      console.error('수신자 추가 실패:', error);
      alert('수신자 추가에 실패했습니다.');
    }
  };

  const handleCancel = () => {
    setIsOpened(false);
    // * 폼 초기화
    setName('');
    setEmail('');
    setMailCategory(0);
    setMailType(0);
  };

  return (
    <Modal isOpened={isOpened}>
      <ModalInner title="Add Schedule Recipient" onClickClose={handleCancel} className="z-50 overflow-visible">
        <div className="flex flex-col gap-4 p-4 overflow-visible">
          <div className="flex flex-col gap-2">
            <Text weight="medium" size="body2" color="primary">
              Name
            </Text>
            <Input
              value={name}
              setValue={setName}
              placeholder="Enter name"
            />
          </div>
          
          <div className="flex flex-col gap-2">
            <Text weight="medium" size="body2" color="primary">
              Email
            </Text>
            <Input
              value={email}
              setValue={setEmail}
              placeholder="Enter email"
              type="email"
            />
          </div>
          
          <div className="relative z-[50] flex flex-col gap-2">
            <Text weight="medium" size="body2" color="primary">
              Category
            </Text>
            <div className="flex flex-col gap-3 sm:flex-row sm:items-stretch sm:gap-4">
              <div className="relative z-[50] min-w-0 flex-1">
                <SelectBox
                  label=""
                  value={mailCategory}
                  setValue={setMailCategory}
                  list={categoryList}
                  placeholder="Select category"
                  bgColor="layer3"
                  className="z-[50]"
                />
              </div>
              <div className="flex min-h-[44px] flex-1 items-center rounded-md border border-border-primary bg-bg-layer2 px-3 py-2.5">
                <Text weight="regular" size="small" color="tertiary" className="leading-relaxed">
                  {categoryHint}
                </Text>
              </div>
            </div>
          </div>
          
          <div className="flex flex-col gap-2 relative z-[10]">
            <Text weight="medium" size="body2" color="primary">
              Type
            </Text>
            <div className="relative z-[10]">
              <SelectBox
                label=""
                value={mailType}
                setValue={setMailType}
                list={typeList}
                placeholder="Select type"
                bgColor="layer3"
                className="z-[10]"
              />
            </div>
          </div>
        </div>
        
        <div className="flex w-full flex-row items-center justify-between bg-bg-transparent_gray_light p-4">
          <Button text="Cancel" onClick={handleCancel} type="secondary" size="large" />
          <Button text="Add" onClick={handleSubmit} type="blue" size="large" />
        </div>
      </ModalInner>
    </Modal>
  );
};

export default AddRecipientModal; 